import { useMemo } from "react";
import Dropdown from "../../atoms/Dropdown";
import {
  paidFreeOptions,
  FormPayloadV2,
  SecondaryDrugWithStatus,
} from "../../data/drugOptionsV2";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import useTherapyDrugs from "../../hooks/useTherapyDrugs";
import Spinner from "../../atoms/Spinner/Spinner";

interface SelectDiseaseV2Props {
  formData: FormPayloadV2;
  setFormData: (formData: FormPayloadV2) => void;
}

const SelectDiseaseV2 = ({ formData, setFormData }: SelectDiseaseV2Props) => {
  // Fetch therapy drugs from API
  const { 
    primaryDrugs, 
    getCancerTypesForPrimary, 
    getSecondaryDrugsForPrimary, 
    isLoading, 
    error 
  } = useTherapyDrugs();

  // Handle Primary Drug Change
  const handlePrimaryDrugChange = (e: any) => {
    const value = e.target.value;
    setFormData({
      primaryDrug: value,
      primaryDrugPaidStatus: 0, // Default to Paid
      secondaryDrugs: [],
      cancerType: "",
      therapy: "",
      indication: "",
      cycle_number: "",
      upcoming_cycle_date: "",
      upcoming_dosage: "",
      notes: "",
      brain_mets: 0,
      isRewardsAssign: false,
    });
  };

  // Handle Primary Drug Paid/Free Status Change
  const handlePrimaryDrugPaidStatusChange = (e: any) => {
    const value = parseInt(e.target.value);
    setFormData({ ...formData, primaryDrugPaidStatus: value as 0 | 1 });
  };

  // Handle Adding New Empty Secondary Drug Row
  const handleAddSecondaryDrugRow = () => {
    const newDrug: SecondaryDrugWithStatus = {
      drug_name: "",
      is_free: 0,
    };

    setFormData({
      ...formData,
      secondaryDrugs: [...(formData.secondaryDrugs || []), newDrug],
    });
  };

  // Handle Deleting a Secondary Drug
  const handleDeleteSecondaryDrug = (index: number) => {
    const updatedDrugs = formData.secondaryDrugs?.filter((_, i) => i !== index) || [];
    setFormData({ ...formData, secondaryDrugs: updatedDrugs });
  };

  // Handle Updating Drug Name in a Row
  const handleUpdateSecondaryDrugName = (index: number, drugName: string) => {
    const updatedDrugs = formData.secondaryDrugs?.map((drug, i) =>
      i === index ? { ...drug, drug_name: drugName } : drug
    );
    setFormData({ ...formData, secondaryDrugs: updatedDrugs });
  };

  // Handle Updating Status of Existing Secondary Drug
  const handleUpdateSecondaryDrugStatus = (index: number, status: 0 | 1) => {
    const updatedDrugs = formData.secondaryDrugs?.map((drug, i) =>
      i === index ? { ...drug, is_free: status } : drug
    );
    setFormData({ ...formData, secondaryDrugs: updatedDrugs });
  };

  // Handle Cancer Type Change
  const handleCancerTypeChange = (e: any) => {
    const value = e.target.value;
    setFormData({ ...formData, cancerType: value, therapy: "", indication: "" });
  };

  // Get Cancer Types for Selected Primary Drug from API
  const cancerTypesForPrimaryDrug = useMemo(() => {
    if (!formData.primaryDrug) return [];
    return getCancerTypesForPrimary(formData.primaryDrug);
  }, [formData.primaryDrug, getCancerTypesForPrimary]);

  // Get Therapies for Selected Cancer Type
  const therapyOptions = useMemo(() => {
    if (!formData.cancerType) return [];
    const selectedCancerType = cancerTypesForPrimaryDrug.find(
      (cancerType) => cancerType.value === formData.cancerType
    );
    return selectedCancerType?.therapies || [];
  }, [cancerTypesForPrimaryDrug, formData.cancerType]);

  // Get Indications for Selected Therapy
  const indicationOptions = useMemo(() => {
    if (!formData.therapy) return [];
    const selectedTherapy = therapyOptions.find(
      (therapy) => therapy.value === formData.therapy
    );
    return selectedTherapy?.indications || [];
  }, [therapyOptions, formData.therapy]);

  // Get Secondary Drugs for Selected Primary Drug from API
  const secondaryDrugsForPrimary = useMemo(() => {
    if (!formData.primaryDrug) return [];
    return getSecondaryDrugsForPrimary(formData.primaryDrug);
  }, [formData.primaryDrug, getSecondaryDrugsForPrimary]);

  // Get available secondary drugs for a specific row (exclude already selected ones)
  const getAvailableSecondaryDrugs = (currentIndex: number) => {
    const selectedDrugNames = formData.secondaryDrugs
      ?.map((d, idx) => (idx !== currentIndex ? d.drug_name : null))
      .filter((name) => name && name !== "") || [];
    
    return secondaryDrugsForPrimary.filter(
      (drug) => !selectedDrugNames.includes(drug.value)
    );
  };

  // Check if there's an incomplete row (empty drug_name)
  const hasIncompleteRow = useMemo(() => {
    if (!formData.secondaryDrugs || formData.secondaryDrugs.length === 0) {
      return false;
    }
    // Check if any row has empty drug_name
    return formData.secondaryDrugs.some((drug) => !drug.drug_name || drug.drug_name === "");
  }, [formData.secondaryDrugs]);

  // Check if all available drugs are already selected
  const allDrugsSelected = useMemo(() => {
    if (isLoading || secondaryDrugsForPrimary.length === 0) {
      return true; // Disable if loading or no drugs available
    }
    
    const selectedDrugNames = (formData.secondaryDrugs || [])
      .map((d) => d.drug_name)
      .filter((name) => name && name !== "");
    
    // All drugs are selected if selected count equals available count
    return selectedDrugNames.length >= secondaryDrugsForPrimary.length;
  }, [formData.secondaryDrugs, secondaryDrugsForPrimary, isLoading]);

  // Disable plus button if there's an incomplete row or all drugs are selected or loading
  const isPlusButtonDisabled = hasIncompleteRow || allDrugsSelected || isLoading;

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <Spinner />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full py-4">
        <p className="text-red-500">Error loading drug options: {error}</p>
      </div>
    );
  }

  return (
    <>
      {/* Primary Drug Selection Section */}
      <div className="w-full">
        <p className="text-bold mt-0 mb-2">Select Primary Drug</p>
        <div className="grid grid-cols-2 gap-4 w-full">
          {/* Primary Drug Dropdown */}
          <Dropdown
            options={primaryDrugs}
            onChange={handlePrimaryDrugChange}
            value={formData.primaryDrug || ""}
            label="Select Primary Drug"
            placeholder="Select Primary Drug"
          />

          {/* Primary Drug Paid/Free Status */}
          {formData.primaryDrug && (
            <Dropdown
              options={paidFreeOptions}
              onChange={handlePrimaryDrugPaidStatusChange}
              value={formData.primaryDrugPaidStatus !== undefined ? String(formData.primaryDrugPaidStatus) : "0"}
              label="Primary Drug Status"
              placeholder="Select Status"
            />
          )}
        </div>
      </div>

 {/* Cancer Type Selection */}
 {formData.primaryDrug && cancerTypesForPrimaryDrug.length > 0 && (
        <div className="w-full mt-4">
          <div className="grid grid-cols-1 gap-4 w-full">
            <Dropdown
              options={cancerTypesForPrimaryDrug}
              onChange={handleCancerTypeChange}
              value={formData.cancerType || ""}
              label="Select Cancer Type"
              placeholder="Select Cancer Type"
            />
          </div>
        </div>
      )}

      {/* Secondary Drug Selection Section */}
      <div className="w-full mt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-bold mt-0 mb-0">Select Secondary Drugs (Optional)</p>
          
          {/* Add Button - Plus Icon */}
          <button
            onClick={handleAddSecondaryDrugRow}
            disabled={isPlusButtonDisabled}
            className={`flex items-center justify-center px-3 py-2 rounded-md ${
              isPlusButtonDisabled
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            style={{ height: "40px", minWidth: "40px" }}
            title={
              hasIncompleteRow
                ? "Please complete the current row first"
                : allDrugsSelected
                ? "All available drugs are already selected"
                : "Add Secondary Drug"
            }
          >
            <PlusOutlined style={{ fontSize: "18px" }} />
          </button>
        </div>

        {/* List of Secondary Drug Rows */}
        {formData.secondaryDrugs && formData.secondaryDrugs.length > 0 && (
          <div className="mt-3 space-y-4">
            {formData.secondaryDrugs.map((drug, index) => (
              <div
                key={index}
                className="flex items-end gap-4 w-full"
              >
                {/* Drug Dropdown */}
                <div className="flex-1" style={{ marginRight: "8px" }}>
                  <Dropdown
                    label="Secondary Drug"
                    placeholder="Select a drug"
                    value={drug.drug_name || ""}
                    onChange={(e: any) =>
                      handleUpdateSecondaryDrugName(index, e.target.value)
                    }
                    options={getAvailableSecondaryDrugs(index).map(
                      (drugOption) => ({
                        label: drugOption.label,
                        value: drugOption.value,
                      }),
                    )}
                  />
                </div>

                {/* Paid/Free Status Dropdown */}
                <div className="flex-1" style={{ marginRight: "12px" }}>
                  <Dropdown
                    options={paidFreeOptions}
                    onChange={(e: any) =>
                      handleUpdateSecondaryDrugStatus(index, parseInt(e.target.value) as 0 | 1)
                    }
                    value={String(drug.is_free)}
                    label="Drug Status"
                    placeholder="Select Status"
                  />
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => handleDeleteSecondaryDrug(index)}
                  className="flex items-center justify-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  style={{ height: "40px", minWidth: "40px" }}
                  title="Remove Drug"
                >
                  <DeleteOutlined style={{ fontSize: "18px" }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SelectDiseaseV2;
